/*
  Tiered storage strategy for ChildNav
  - Provides a documented interface + simple factory stubs mapping layers to recommended platform implementations.
  - This file lives in `packages/shared` so app code can import the strategy and small wrappers.

  NOTE: This file intentionally contains lightweight stubs and documentation. We'll implement concrete wrappers
  in `packages/mobile` and `packages/web` where native adapters are available.
*/

export type NavigationTab = string;

// Storage layer type names
export type KeyValueStore = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

export type StructuredStore = {
  // Very small API surface here; real stores (RxDB/WatermelonDB/Realm) will have more.
  find: <T = any>(query?: any) => Promise<T[]>;
  upsert: <T = any>(doc: T) => Promise<void>;
  remove: (id: string) => Promise<void>;
};

export type MediaStore = {
  // storePath should be a local path or key; download returns that path/key
  downloadAndCache: (remoteUrl: string, opts?: { ttlSeconds?: number }) => Promise<string>;
  getLocalPath: (remoteUrl: string) => Promise<string | null>;
  remove: (remoteUrl: string) => Promise<void>;
};

export type SecureStore = {
  setSecureItem: (key: string, value: string) => Promise<void>;
  getSecureItem: (key: string) => Promise<string | null>;
  removeSecureItem: (key: string) => Promise<void>;
};

export interface StorageLayer {
  keyValue: KeyValueStore; // Fast, simple key-value (AsyncStorage / localStorage / MMKV)
  structured: StructuredStore; // Structured, queryable data (WatermelonDB / RxDB / Realm)
  media: MediaStore; // Large binary data (FileSystem / IndexedDB)
  secure: SecureStore; // Encrypted sensitive data (Keychain / Encrypted AsyncStorage)
}

/*
  Platform mapping recommendations (short version):

  - KeyValue (small JSON blobs, caches, flags):
    * Mobile: @react-native-async-storage/async-storage or react-native-mmkv for performance
    * Web: localStorage or IndexedDB (via wrappers)

  - Structured (larger collections, queries):
    * Mobile: WatermelonDB (fast on top of SQLite) or Realm (embedded DB) if you need complex queries
    * Web: RxDB (IndexedDB) or PouchDB
    * Note: If app data is small/simple, prefer JSON in key-value + in-memory store (Zustand) instead of full DB.

  - Media (images/videos):
    * Mobile: react-native-fs or expo-file-system to store files on disk; store metadata in keyValue/structured store
    * Web: IndexedDB (via idb) or the Cache API (service worker) and keep metadata in IndexedDB/localStorage

  - Secure:
    * Mobile: react-native-keychain or react-native-encrypted-storage
    * Web: browser storage is not secure; keep minimal sensitive tokens server-sided or use Web Crypto + IndexedDB
*/

// Basic no-op implementations to make imports safe in shared code. Replace with real implementations per-platform.

const noopKeyValue: KeyValueStore = {
  async getItem(_key: string) { return null; },
  async setItem(_key: string, _value: string) { return; },
  async removeItem(_key: string) { return; },
};

const noopStructured: StructuredStore = {
  async find() { return []; },
  async upsert() { return; },
  async remove() { return; },
};

const noopMedia: MediaStore = {
  async downloadAndCache() { throw new Error('No media store available on this platform'); },
  async getLocalPath() { return null; },
  async remove() { return; },
};

const noopSecure: SecureStore = {
  async setSecureItem() { return; },
  async getSecureItem() { return null; },
  async removeSecureItem() { return; },
};

// In-memory key-value store for development/testing
const createInMemoryKeyValue = (): KeyValueStore => {
  const store = new Map<string, string>();
  
  return {
    async getItem(key: string) { 
      const value = store.get(key) || null;
      console.log(`🔍 InMemoryKeyValue.getItem('${key}') = ${value ? `${value.length} chars` : 'null'}`);
      return value;
    },
    async setItem(key: string, value: string) { 
      store.set(key, value);
      console.log(`💾 InMemoryKeyValue.setItem('${key}', ${value.length} chars) - Store now has ${store.size} items`);
    },
    async removeItem(key: string) { 
      const existed = store.delete(key);
      console.log(`🗑️ InMemoryKeyValue.removeItem('${key}') - ${existed ? 'Removed' : 'Not found'}`);
    },
  };
};

export const createInMemoryStorageLayer = (): StorageLayer => ({
  keyValue: createInMemoryKeyValue(),
  structured: noopStructured,
  media: noopMedia,
  secure: noopSecure,
});

/*
  Factory notes / integration hints:

  1) Mobile implementation example (implement inside packages/mobile):
     - KeyValue: use AsyncStorage or MMKV with a small wrapper that serializes JSON.
     - Structured: if you need advanced queries, wire up WatermelonDB (uses SQLite) or Realm; otherwise use
       RxDB with a SQLite adapter or keep collections in JSON persisted via AsyncStorage.
     - Media: use react-native-fs or expo-file-system to download files to app's document/cache dir; maintain metadata (remoteUrl -> localPath, expiry) in keyValue/structured store.
     - Secure: use react-native-keychain or react-native-encrypted-storage for tokens and secrets.

  2) Web implementation example (implement inside packages/web):
     - KeyValue: localStorage wrapper for small items; use `idb-keyval` for more reliability.
     - Structured: use RxDB backed by IndexedDB for queryable collections.
     - Media: use `idb` to store Blobs in IndexedDB or use the Cache API; store metadata in IndexedDB.
     - Secure: browsers can't truly secure storage; minimize sensitive client-side storage, or use SubtleCrypto to encrypt + store keys in memory/session.

  3) Sync pattern (offline-first):
     - Cache backend responses in keyValue/structured stores.
     - Mark items with `dirty` flags and a `lastModified` timestamp.
     - On network available, run a sync worker that sends local changes to backend and reconciles updates.

  4) Media caching policy:
     - Keep thumbnails and small images in keyValue or indexedDB with TTL.
     - Keep full-size media on disk (mobile) or IndexedDB (web); evict by LRU or TTL when storage limit reached.

  5) When to choose SQLite/WatermelonDB:
     - If you need fast queries over thousands of records or complex joins, choose a structured DB (SQLite + WatermelonDB or Realm).
     - If storage needs are moderate (< few thousand small records) prefer key-value + in-memory store (Zustand) for simplicity.
*/

export default {
  createInMemoryStorageLayer,
  noopKeyValue,
  noopStructured,
  noopMedia,
  noopSecure,
};
